// Copyright 2025, University of Maryland, All Rights Reserved
import { useMemo, useState } from 'react';
import {
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Label,
} from '@/components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUsersApi, type UsersApi } from '../services/users-api';
import { UserCog } from 'lucide-react';
import type { UserProfileResponse } from '../types/user';
import { useAuth } from '../context/AuthContext';
import { AppConfigProvider } from '../context/AppConfigContext';
import { clearWorkspaceRouteParams } from '../lib/workspace-utils';
import { useUserProfile, USER_PROFILE_QUERY_KEY } from '../hooks/use-user-profile';
import { UserTypeahead } from './users/UserTypeahead';

interface ImpersonationMenuItemProps {
  app_id?: string;
  debug?: boolean;
  /** Optional API instance for testing */
  usersApi?: UsersApi;
}

interface ImpersonationMenuItemInnerProps {
  usersApi?: UsersApi;
}

/**
 * Internal component that uses the hooks
 */
function ImpersonationMenuItemInner({ usersApi: injectedApi }: ImpersonationMenuItemInnerProps) {
  const { isAuthenticated, isGlobalAdmin } = useAuth();
  const queryClient = useQueryClient();
  const defaultApi = useUsersApi();
  const usersApi = injectedApi ?? defaultApi;
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [impersonateUserId, setImpersonateUserId] = useState("");

  // Fetch user profile to check if already impersonating using standardized hook
  const { data: userProfile } = useUserProfile(isAuthenticated && isGlobalAdmin);

  // Fetch all users for impersonation
  const { data: allUsers = [] } = useQuery<UserProfileResponse[]>({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.getAllUsers(),
    enabled: isGlobalAdmin && impersonateDialogOpen,
  });

  const allUserIds = useMemo(() => allUsers.map(u => u.userId), [allUsers]);
  const isExistingUser = useMemo(
    () => allUserIds.includes(impersonateUserId.trim()),
    [allUserIds, impersonateUserId],
  );

  // Set impersonation mutation
  const impersonateMutation = useMutation({
    mutationFn: (targetUserId: string | null) => usersApi.setUserImpersonation(targetUserId),
    onSuccess: () => {
      // cm-coding: The current hash belongs to the administrator. The effective user's
      // saved workspace must decide the next route after the reload.
      clearWorkspaceRouteParams();
      queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
      // Force a full page reload to reset the auth context with new impersonation
      window.location.reload();
    },
  });

  const isImpersonating = userProfile?.impersonatedBy != null;

  const handleStartImpersonation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setImpersonateDialogOpen(true);
    setImpersonateUserId("");
  };

  const handleImpersonate = () => {
    if (isExistingUser) {
      impersonateMutation.mutate(impersonateUserId.trim());
      setImpersonateDialogOpen(false);
    }
  };

  // Don't render if already impersonating
  if (isImpersonating) {
    return null;
  }

  return (
    <>
      <DropdownMenuItem
        data-testid="admin-impersonate-open"
        className="px-2 py-2 text-sm"
        onSelect={(e) => {
          e.preventDefault();
          handleStartImpersonation(e as unknown as React.MouseEvent);
        }}
      >
        <UserCog className="mr-2 h-4 w-4" />
        <span>Impersonate User</span>
      </DropdownMenuItem>

      {/* Impersonation Dialog */}
      <Dialog open={impersonateDialogOpen} onOpenChange={setImpersonateDialogOpen}>
        <DialogContent className="max-w-md" data-testid="admin-impersonate-dialog">
          <DialogHeader>
            <DialogTitle data-testid="admin-impersonate-title">Impersonate User</DialogTitle>
            <DialogDescription>
              Enter the user ID to impersonate. You will see the application as that user would see it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="impersonate_user_id">User ID</Label>
              <UserTypeahead
                value={impersonateUserId}
                onValueChange={setImpersonateUserId}
                suggestions={allUserIds}
                placeholder="Search for a user..."
                autoFocus
              />
              {impersonateUserId.trim() && !isExistingUser && (
                <p className="text-xs text-destructive" data-testid="admin-impersonate-not-found">
                  No user found with that ID.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                The page will reload once impersonation is activated.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-testid="admin-impersonate-cancel"
              onClick={() => setImpersonateDialogOpen(false)}
              disabled={impersonateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImpersonate}
              disabled={!isExistingUser || impersonateMutation.isPending}
              data-testid="admin-impersonate-submit"
            >
              {impersonateMutation.isPending ? 'Activating...' : 'Start Impersonation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * ImpersonationMenuItem Component
 *
 * A reusable dropdown menu item that allows global admins to impersonate other users.
 * When clicked, it opens a dialog where the admin can select a user to impersonate.
 *
 * @example
 * ```tsx
 * import { ImpersonationMenuItem } from '@umd-dit-ai-workspace/workspaces-component';
 *
 * <DropdownMenu>
 *   <DropdownMenuContent>
 *     {isGlobalAdmin && <ImpersonationMenuItem app_id="docreview" />}
 *   </DropdownMenuContent>
 * </DropdownMenu>
 * ```
 */
/**
 * ImpersonationMenuItem Component
 *
 * Note: This component now relies on a parent QueryClientProvider.
 * The parent app must wrap this component with QueryClientProvider.
 */
export function ImpersonationMenuItem({ app_id = 'default', debug = false, usersApi }: ImpersonationMenuItemProps) {
  return (
    <AppConfigProvider app_id={app_id} debug={debug}>
      <ImpersonationMenuItemInner usersApi={usersApi} />
    </AppConfigProvider>
  );
}
